import { Button, Link, Typography } from '@/components';
import { useSidebar } from '@/components/ui/sidebar';
import { Route } from '@/constants';
import { cn } from '@/utils/styles';

type FloatingActionButtonsProps = {
  readonly opportunityId: string;
  readonly teaserId?: string;
  readonly opportunityBelongsToMyCompany: boolean | undefined;
  readonly canSendForRevision: boolean;
  readonly canShowOpenButton: boolean | undefined;
  readonly handleOnClickTeaser: () => void;
  readonly handleOnClick: () => void;
  readonly sendForRevision: () => void;
  readonly saveForLater: () => void;
  readonly t: (key: string) => string;
};

export function FloatingActionButtons({
  opportunityId,
  teaserId,
  opportunityBelongsToMyCompany,
  canSendForRevision,
  canShowOpenButton,
  handleOnClickTeaser,
  handleOnClick,
  sendForRevision,
  saveForLater,
  t,
}: FloatingActionButtonsProps) {
  const { state } = useSidebar();
  const isCollapsed = state === 'collapsed';

  // Use cases:
  // 1. It's my turn and it's in draft mode → Management buttons
  const showManagementButtons = opportunityBelongsToMyCompany && canSendForRevision;

  // 2. It's my turn, but it's NOT in the draft → You can show Open + Download
  const showOwnerButtons = opportunityBelongsToMyCompany && !canSendForRevision;

  // 3. Not my opportunity → Buttons of interest
  const showInterestButtons = !opportunityBelongsToMyCompany;

  return (
    <div
      className={cn(
        'fixed right-0 bottom-0 z-50 border-t border-neutral-100 bg-white p-4 shadow-lg lg:hidden',
        'left-0',
        isCollapsed ? 'md:left-[60px]' : 'md:left-[220px]'
      )}
      style={{ transition: 'left 150ms ease' }}
    >
      <div className="flex gap-3">
        {/* Case 1: It's my turn in the draft - Management buttons */}
        {showManagementButtons && (
          <>
            <Button size="md" color="secondary" onClick={sendForRevision} className="flex-1">
              {t('Send for revision')}
            </Button>
            <Button size="md" color="neutral" onClick={saveForLater} className="flex-1">
              {t('Save for later')}
            </Button>
          </>
        )}

        {/* Case 2: It's my opportunity (NOT draft) - Open + Download */}
        {showOwnerButtons && (
          <>
            {canShowOpenButton && (
              <Link
                to={`${Route.Opportunities}/${opportunityId}`}
                target="_blank"
                className="hover:bg-grey-50 flex flex-1 items-center justify-center rounded-md bg-gray-200 px-4 py-2"
              >
                <Typography as="span" className="font-heading text-sm" weight="semibold">
                  {t('Open Opportunity')}
                </Typography>
              </Link>
            )}

            {/* Download Teaser - if a teaser exists */}
            {teaserId && (
              <Button
                size="md"
                color="neutral"
                icon="download"
                onClick={handleOnClickTeaser}
                className={cn(canShowOpenButton ? 'flex-1' : 'w-full')}
              >
                {t('Download Teaser')}
              </Button>
            )}
          </>
        )}

        {/* Case 3: Not the right opportunity for me - Download + Express Interest */}
        {showInterestButtons && (
          <>
            {teaserId && (
              <Button size="md" color="neutral" icon="download" onClick={handleOnClickTeaser} className="flex-1">
                {t('Download Teaser')}
              </Button>
            )}
            <Button size="md" color="secondary" icon="my-offers" onClick={handleOnClick} className="flex-1">
              {t('Express Interest')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
